import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Modal,
  Form,
  message,
  Tag,
  Row,
  Col,
  Switch,
  Typography,
  Divider,
  Popconfirm,
  Badge,
  Tabs,
  Collapse,
  Tooltip
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  EyeOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons'
import { marketingAPIService } from '../services/api'
import { api } from '../services/api'

const { TextArea } = Input
const { Option } = Select
const { Title, Text } = Typography
const { Panel } = Collapse

// Available email template variables
const EMAIL_VARIABLES = [
  { tag: '{{firstName}}', description: 'Contact first name', example: 'John' },
  { tag: '{{lastName}}', description: 'Contact last name', example: 'Doe' },
  { tag: '{{fullName}}', description: 'Full name (firstName lastName)', example: 'John Doe' },
  { tag: '{{email}}', description: 'Contact email address', example: 'john@example.com' },
  { tag: '{{companyName}}', description: 'Company/business name', example: 'Example Business' },
  { tag: '{{subscriptionPlan}}', description: 'Subscription plan name', example: 'Professional' },
  { tag: '{{businessName}}', description: 'ServiceFlow business name', example: 'ServiceFlow Pro' },
  { tag: '{{businessPhone}}', description: 'Business phone number', example: '555-1234' },
  { tag: '{{businessEmail}}', description: 'Business email address', example: 'info@example.com' },
  { tag: '{{unsubscribeUrl}}', description: 'Unsubscribe link (required in footer)', example: 'https://serviceflow-pro.com/unsubscribe?id=123' }
]

// Email Body Editor Component with Preview
const EmailBodyEditor: React.FC<{
  field: any
  form: any
  value?: string
  onChange?: (value: string) => void
}> = ({ field, form, value, onChange }) => {
  // Try to get value from props first, then from form watch, then empty string
  const watchedValue = Form.useWatch(['steps', field.name, 'body'], form)
  const bodyValue = value ?? watchedValue ?? ''
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  
  // Handle onChange - need to update form field properly
  const handleChange = (newValue: string) => {
    if (onChange) {
      onChange(newValue)
    } else {
      // Fallback: update form directly
      const currentStepValue = form.getFieldValue(['steps', field.name]) || {}
      form.setFieldsValue({
        steps: {
          ...form.getFieldValue('steps'),
          [field.name]: {
            ...currentStepValue,
            body: newValue
          }
        }
      })
    }
  }
  
  // Sample values for preview
  const sampleValues: Record<string, string> = {
    firstName: 'John',
    lastName: 'Doe',
    fullName: 'John Doe',
    email: 'john@example.com',
    companyName: 'Example Business',
    subscriptionPlan: 'Professional',
    businessName: 'ServiceFlow Pro',
    businessPhone: '555-1234',
    businessEmail: 'info@example.com',
    unsubscribeUrl: '#unsubscribe'
  }
  
  // Replace variables in HTML for preview
  const previewHtml = bodyValue.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return sampleValues[key] || match
  })
  
  // Insert variable into textarea at cursor position
  const insertVariable = (variable: string) => {
    const textarea = textareaRef.current
    if (!textarea) {
      // Fallback: just append to end if textarea not found
      const currentValue = bodyValue || ''
      handleChange(currentValue + variable)
      return
    }
    
    const start = textarea.selectionStart || 0
    const end = textarea.selectionEnd || 0
    const currentValue = bodyValue || ''
    const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end)
    
    handleChange(newValue)
    
    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.focus()
      const newPos = start + variable.length
      textarea.setSelectionRange(newPos, newPos)
    }, 0)
  }
  
  return (
    <div>
      {/* Variable Tags Panel */}
      <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f8f9fa' }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Available Variables:</Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {EMAIL_VARIABLES.map((variable) => (
            <Tooltip key={variable.tag} title={variable.description}>
              <Tag
                style={{ cursor: 'pointer', marginBottom: 4 }}
                color="blue"
                onClick={() => insertVariable(variable.tag)}
              >
                {variable.tag}
              </Tag>
            </Tooltip>
          ))}
        </div>
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 8 }}>
          Click any tag above to insert it at your cursor position
        </Text>
      </Card>
      
      {/* Editor/Preview Tabs */}
      <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key as 'edit' | 'preview')}>
        <Tabs.TabPane tab="Edit HTML" key="edit">
          <TextArea
            ref={textareaRef}
            rows={12}
            placeholder="HTML email body. Use variables like {{firstName}}, {{companyName}}, etc. Click tags above to insert."
            value={bodyValue}
            onChange={(e) => {
              handleChange(e.target.value)
            }}
            style={{ fontFamily: 'monospace', fontSize: '13px' }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Preview" key="preview">
          <div
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              backgroundColor: '#ffffff',
              minHeight: '300px',
              padding: '20px',
              overflow: 'auto'
            }}
          >
            {bodyValue ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: previewHtml
                }}
                style={{
                  maxWidth: '600px',
                  margin: '0 auto'
                }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">Enter HTML in the Edit tab to see preview</Text>
              </div>
            )}
          </div>
        </Tabs.TabPane>
      </Tabs>
    </div>
  )
}

// Trigger Conditions Builder Component
const TriggerConditionsBuilder: React.FC<{
  triggerEvent?: string
  value?: any
  onChange?: (value: any) => void
}> = ({ triggerEvent, value, onChange }) => {
  const conditions = value || {}
  
  const handleChange = (key: string, val: number | null | string) => {
    const newConditions = { ...conditions }
    if (val === null || val === undefined || val === '' || (typeof val === 'string' && val.trim() === '')) {
      delete newConditions[key]
    } else {
      newConditions[key] = typeof val === 'number' ? val : parseInt(String(val), 10)
    }
    // Remove empty object
    const hasValues = Object.keys(newConditions).length > 0
    onChange?.(hasValues ? newConditions : null)
  }

  if (!triggerEvent) {
    return (
      <Text type="secondary" style={{ fontStyle: 'italic' }}>
        Select a trigger event to configure conditions
      </Text>
    )
  }

  const renderConditionField = (label: string, key: string, unit: string = 'days') => {
    return (
      <Row gutter={8} style={{ marginBottom: 8 }}>
        <Col span={10}>
          <Text style={{ lineHeight: '32px' }}>{label}:</Text>
        </Col>
        <Col span={12}>
          <Input
            type="number"
            min={0}
            value={conditions[key] || ''}
            onChange={(e) => {
              const val = e.target.value ? parseInt(e.target.value) : null
              handleChange(key, val)
            }}
            placeholder={`Number of ${unit}`}
            addonAfter={unit}
          />
        </Col>
      </Row>
    )
  }

  return (
    <Card size="small" style={{ backgroundColor: '#fafafa' }}>
      {triggerEvent === 'contact_inactive' && (
        <>
          {renderConditionField('Days Since Last Activity', 'daysSinceLastActivity', 'days')}
          {renderConditionField('Days Since Last Login', 'daysSinceLastLogin', 'days')}
          {renderConditionField('Days Since Last Service', 'daysSinceLastService', 'days')}
        </>
      )}
      
      {triggerEvent === 'subscription_trial_ending' && (
        <>
          {renderConditionField('Days Before Trial End', 'daysBeforeTrialEnd', 'days')}
        </>
      )}
      
      {triggerEvent === 'estimate_abandoned' && (
        <>
          {renderConditionField('Hours Since Created', 'hoursSinceCreated', 'hours')}
        </>
      )}
      
      {triggerEvent === 'invoice_abandoned' && (
        <>
          {renderConditionField('Days Since Created', 'daysSinceCreated', 'days')}
        </>
      )}
      
      {triggerEvent === 'service_completed' && (
        <>
          {renderConditionField('Days After Completion', 'daysAfterCompletion', 'days')}
        </>
      )}

      {!['contact_inactive', 'subscription_trial_ending', 'estimate_abandoned', 'invoice_abandoned', 'service_completed'].includes(triggerEvent) && (
        <Text type="secondary" style={{ fontStyle: 'italic' }}>
          No additional conditions available for this trigger event
        </Text>
      )}

      {Object.keys(conditions).length > 0 && (
        <div style={{ marginTop: 12, padding: 8, backgroundColor: '#e6f7ff', borderRadius: 4 }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <strong>JSON:</strong> {JSON.stringify(conditions)}
          </Text>
        </div>
      )}
    </Card>
  )
}

// Target Segments Builder Component
// Note: This component uses hooks, so it must be used as a proper React component, not a function
const TargetSegmentsBuilder: React.FC<{
  value?: any
  onChange?: (value: any) => void
}> = ({ value, onChange }) => {
  const segments = value || {}
  
  // Fetch subscription plans from API
  const { data: subscriptionPlansData, isLoading: plansLoading } = useQuery(
    'admin-subscription-plans',
    async () => {
      try {
        const response = await api.get('/subscription-plans')
        return response.data.plans || response.data || []
      } catch (error) {
        console.error('Failed to fetch subscription plans:', error)
        // Fallback to common plans
        return [
          { name: 'Starter', id: 'starter' },
          { name: 'Professional', id: 'professional' },
          { name: 'Enterprise', id: 'enterprise' },
          { name: 'Free Trial', id: 'free_trial' }
        ]
      }
    }
  )
  
  // Get subscription plan names (normalized to lowercase for matching)
  const subscriptionPlans = subscriptionPlansData?.map((plan: any) => ({
    value: plan.name?.toLowerCase() || plan.id?.toLowerCase(),
    label: plan.name || plan.id
  })) || [
    { value: 'starter', label: 'Starter' },
    { value: 'professional', label: 'Professional' },
    { value: 'enterprise', label: 'Enterprise' },
    { value: 'free trial', label: 'Free Trial' }
  ]
  
  // Fetch industry types from API (using correct endpoint)
  const { data: industryTypesData } = useQuery(
    'industry-template-options',
    async () => {
      try {
        // Use the public API endpoint (not admin endpoint)
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/industry-templates`)
        const data = await response.json()
        return data.options || []
      } catch (error) {
        console.error('Failed to fetch industry types:', error)
        // Fallback to common industry types
        return [
          { value: 'home_services', label: 'Home Services' },
          { value: 'general_contractor', label: 'General Contractor' },
          { value: 'hvac', label: 'HVAC' },
          { value: 'plumbing', label: 'Plumbing' },
          { value: 'electrical', label: 'Electrical' },
          { value: 'roofing', label: 'Roofing' },
          { value: 'painting', label: 'Painting' },
          { value: 'landscaping', label: 'Landscaping' },
          { value: 'carpentry', label: 'Carpentry' },
          { value: 'flooring', label: 'Flooring' },
          { value: 'appliance_repair', label: 'Appliance Repair' },
          { value: 'garage_door', label: 'Garage Door' },
          { value: 'windows_doors', label: 'Windows & Doors' },
          { value: 'other', label: 'Other' }
        ]
      }
    }
  )
  
  const industryTypes = industryTypesData || []
  
  // Subscription statuses (from database schema)
  const subscriptionStatuses = [
    { value: 'active', label: 'Active' },
    { value: 'trial', label: 'Trial' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'expired', label: 'Expired' },
    { value: 'inactive', label: 'Inactive' }
  ]

  const handleArrayChange = (key: string, selectedValues: string[]) => {
    const newSegments = { ...segments }
    if (!selectedValues || selectedValues.length === 0) {
      delete newSegments[key]
    } else {
      newSegments[key] = selectedValues
    }
    // Remove empty object
    const hasValues = Object.keys(newSegments).length > 0
    onChange?.(hasValues ? newSegments : null)
  }

  return (
    <Card size="small" style={{ backgroundColor: '#fafafa' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Subscription Plan:</Text>
          <Select
            mode="multiple"
            placeholder={plansLoading ? "Loading plans..." : "Select subscription plans (leave empty for all)"}
            style={{ width: '100%' }}
            value={segments.subscriptionPlan || []}
            onChange={(values) => handleArrayChange('subscriptionPlan', values)}
            allowClear
            loading={plansLoading}
            disabled={plansLoading}
          >
            {subscriptionPlans.map((plan: any) => (
              <Option key={plan.value || plan} value={plan.value || plan}>
                {plan.label || plan}
              </Option>
            ))}
          </Select>
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Subscription Status:</Text>
          <Select
            mode="multiple"
            placeholder="Select subscription statuses (leave empty for all)"
            style={{ width: '100%' }}
            value={segments.subscriptionStatus || []}
            onChange={(values) => handleArrayChange('subscriptionStatus', values)}
            allowClear
          >
            {subscriptionStatuses.map((status: any) => (
              <Option key={status.value || status} value={status.value || status}>
                {status.label || status}
              </Option>
            ))}
          </Select>
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Industry Type:</Text>
          <Select
            mode="multiple"
            placeholder="Select industry types (leave empty for all)"
            style={{ width: '100%' }}
            value={segments.industryType || []}
            onChange={(values) => handleArrayChange('industryType', values)}
            allowClear
          >
            {industryTypes.map((industry: any) => (
              <Option key={industry.value || industry} value={industry.value || industry}>
                {industry.label || industry}
              </Option>
            ))}
          </Select>
        </div>

        {Object.keys(segments).length > 0 && (
          <div style={{ marginTop: 8, padding: 8, backgroundColor: '#e6f7ff', borderRadius: 4 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <strong>JSON:</strong> {JSON.stringify(segments)}
            </Text>
          </div>
        )}

        {Object.keys(segments).length === 0 && (
          <Text type="secondary" style={{ fontStyle: 'italic' }}>
            Leave empty to target all contacts. Select filters above to narrow the audience.
          </Text>
        )}
      </Space>
    </Card>
  )
}

interface Business {
  id: string
  name: string
}

interface TemplateStep {
  id: string
  type: 'email'
  delayDays: number
  delayHours?: number
  subject: string
  body: string
  previewText?: string
}

interface MarketingTemplate {
  id: string
  businessId: string
  name: string
  description?: string
  templateName?: string
  campaignType: 'drip' | 'one-time' | 'triggered'
  steps: TemplateStep[]
  isActive: boolean
  isDefault: boolean
  triggerEvent?: string | null
  triggerConditions?: any
  targetSegments?: any
  createdAt: string
  updatedAt: string
}

const MarketingTemplatesPage: React.FC = () => {
  const { businessId } = useParams<{ businessId?: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const [templateType, setTemplateType] = useState<'business' | 'system'>('business')
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | undefined>(
    businessId === 'system' ? undefined : businessId
  )
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<MarketingTemplate | null>(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewTemplate, setPreviewTemplate] = useState<MarketingTemplate | null>(null)
  const [form] = Form.useForm()
  const [stepForm] = Form.useForm()
  
  // Watch form values at top level (hooks must be called unconditionally)
  const watchedCampaignType = Form.useWatch('campaignType', form)
  const watchedTriggerEvent = Form.useWatch('triggerEvent', form)
  const watchedTriggerConditions = Form.useWatch('triggerConditions', form)
  const watchedTargetSegments = Form.useWatch('targetSegments', form)

  // Set template type based on businessId from URL
  useEffect(() => {
    if (businessId === 'system') {
      setTemplateType('system')
      setSelectedBusinessId(undefined)
    } else if (businessId) {
      setTemplateType('business')
      setSelectedBusinessId(businessId)
    }
  }, [businessId])

  // Auto-populate steps when campaign type is selected
  useEffect(() => {
    if (!watchedCampaignType || editingTemplate) {
      // Don't auto-populate if editing existing template or no type selected
      return
    }

    // Fetch default templates to see if there's a matching one
    const loadDefaultSteps = async () => {
      try {
        const response = await marketingAPIService.getDefaultTemplates()
        
        // Response structure: { success: true, templates: {...}, templateNames: [...] }
        const defaultTemplates = response.templates || {}
        
        // Find a matching template by campaign type
        const matchingTemplate = Object.values(defaultTemplates).find(
          (template: any) => template.campaignType === watchedCampaignType
        ) as { steps?: any[]; name?: string; description?: string } | undefined

        if (matchingTemplate && matchingTemplate.steps && Array.isArray(matchingTemplate.steps) && matchingTemplate.steps.length > 0) {
          // Get current form values
          const currentSteps = form.getFieldValue('steps')
          const currentName = form.getFieldValue('name')
          const currentDesc = form.getFieldValue('description')
          
          // Always update steps when campaign type changes (user can edit them after)
          form.setFieldsValue({
            steps: matchingTemplate.steps.map((step: any, index: number) => ({
              ...step,
              id: step.id || `step-${Date.now()}-${index}`
            }))
          })
          
          // Only set name and description if they're empty
          if (!currentName && matchingTemplate.name) {
            form.setFieldsValue({ name: String(matchingTemplate.name) })
          }
          
          if (!currentDesc && matchingTemplate.description) {
            form.setFieldsValue({ description: String(matchingTemplate.description) })
          }

          // Show message indicating template was loaded/updated
          if (!currentSteps || !Array.isArray(currentSteps) || currentSteps.length === 0) {
            message.info(`Loaded ${matchingTemplate.steps.length} steps from default ${watchedCampaignType} template`)
          } else {
            message.info(`Updated to ${watchedCampaignType} template with ${matchingTemplate.steps.length} steps`)
          }
        }
      } catch (error) {
        console.error('Failed to load default templates:', error)
        // Silently fail - don't show error to user as this is just a convenience feature
      }
    }

    // Small delay to ensure form is ready
    const timer = setTimeout(loadDefaultSteps, 100)
    return () => clearTimeout(timer)
  }, [watchedCampaignType, editingTemplate, form])

  // Fetch businesses for selector
  const { data: businessesData } = useQuery(
    'businesses-list',
    async () => {
      const response = await api.get('/businesses?limit=1000')
      return response.data.businesses as Business[]
    },
    { enabled: !businessId }
  )

  // Fetch templates for selected business or system
  const { data: templatesData, isLoading: templatesLoading } = useQuery(
    ['business-templates', templateType, selectedBusinessId],
    () => marketingAPIService.getBusinessTemplates(
      templateType === 'system' ? 'system' : selectedBusinessId!
    ),
    { enabled: templateType === 'system' || !!selectedBusinessId }
  )

  // Fetch default templates
  const { data: defaultTemplatesData } = useQuery(
    'default-templates',
    () => marketingAPIService.getDefaultTemplates()
  )

  // Fetch selected business details
  const { data: selectedBusiness } = useQuery(
    ['business', selectedBusinessId],
    async () => {
      if (!selectedBusinessId) return null
      const response = await api.get(`/businesses/${selectedBusinessId}`)
      return response.data as Business
    },
    { enabled: !!selectedBusinessId }
  )

  // Handle template type change
  const handleTemplateTypeChange = (type: 'business' | 'system') => {
    setTemplateType(type)
    if (type === 'system') {
      setSelectedBusinessId(undefined)
      navigate('/marketing/templates/system')
    } else {
      navigate('/marketing/templates')
    }
  }

  const createMutation = useMutation(
    (data: any) => marketingAPIService.createTemplate(
      templateType === 'system' ? 'system' : selectedBusinessId!,
      data
    ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['business-templates', templateType, selectedBusinessId])
        setShowCreateModal(false)
        form.resetFields()
        message.success('Template created successfully')
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to create template')
      }
    }
  )

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => marketingAPIService.updateTemplate(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['business-templates', templateType, selectedBusinessId])
        setEditingTemplate(null)
        setShowCreateModal(false)
        form.resetFields()
        message.success('Template updated successfully')
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to update template')
      }
    }
  )

  const deleteMutation = useMutation(
    (id: string) => marketingAPIService.deleteTemplate(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['business-templates', templateType, selectedBusinessId])
        message.success('Template deleted successfully')
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to delete template')
      }
    }
  )

  const handleCreate = () => {
    if (templateType === 'business' && !selectedBusinessId) {
      message.warning('Please select a business first')
      return
    }
    setEditingTemplate(null)
    form.resetFields()
    setShowCreateModal(true)
  }

  const handleEdit = (template: MarketingTemplate) => {
    setEditingTemplate(template)
    form.setFieldsValue({
      name: template.name,
      description: template.description,
      templateName: template.templateName,
      campaignType: template.campaignType,
      isActive: template.isActive,
      steps: template.steps,
      triggerEvent: template.triggerEvent,
      triggerConditions: template.triggerConditions 
        ? JSON.stringify(template.triggerConditions, null, 2)
        : undefined,
      targetSegments: template.targetSegments
        ? JSON.stringify(template.targetSegments, null, 2)
        : undefined
    })
    setShowCreateModal(true)
  }

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id)
  }

  const handleSubmit = async (values: any) => {
    if (!values.steps || values.steps.length === 0) {
      message.error('At least one step is required')
      return
    }

    // Parse JSON fields
    const submitData = { ...values }
    
    if (typeof submitData.triggerConditions === 'string' && submitData.triggerConditions.trim()) {
      try {
        submitData.triggerConditions = JSON.parse(submitData.triggerConditions)
      } catch (e) {
        message.error('Trigger Conditions must be valid JSON')
        return
      }
    } else if (!submitData.triggerConditions) {
      submitData.triggerConditions = null
    }
    
    if (typeof submitData.targetSegments === 'string' && submitData.targetSegments.trim()) {
      try {
        submitData.targetSegments = JSON.parse(submitData.targetSegments)
      } catch (e) {
        message.error('Target Segments must be valid JSON')
        return
      }
    } else if (!submitData.targetSegments) {
      submitData.targetSegments = null
    }

    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data: submitData })
    } else {
      createMutation.mutate(submitData)
    }
  }

  const handlePreview = (template: MarketingTemplate) => {
    setPreviewTemplate(template)
    setShowPreviewModal(true)
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: MarketingTemplate) => (
        <Space direction="vertical" size="small">
          <Text strong>{text}</Text>
          {record.description && (
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.description}</Text>
          )}
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'campaignType',
      key: 'campaignType',
      render: (type: string) => {
        const colorMap: Record<string, string> = {
          'drip': 'blue',
          'one-time': 'orange',
          'triggered': 'green',
          'nurturing': 'purple',
          're-engagement': 'cyan',
          'win-back': 'magenta',
          'abandonment': 'red',
          'welcome': 'lime'
        }
        const displayName = type.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ')
        return (
          <Tag color={colorMap[type] || 'default'}>
            {displayName}
          </Tag>
        )
      }
    },
    {
      title: 'Steps',
      dataIndex: 'steps',
      key: 'steps',
      render: (steps: TemplateStep[]) => (
        <Badge count={steps.length} showZero />
      )
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: MarketingTemplate) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
            size="small"
          >
            Preview
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete template?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  const renderStepEditor = () => {
    // Don't use hooks here - get value from form directly in render
    return (
      <Form.List name="steps">
        {(fields, { add, remove }) => (
          <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={5}>Campaign Steps</Title>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => add({
                  id: `step-${Date.now()}`,
                  type: 'email',
                  delayDays: 0,
                  delayHours: 0,
                  subject: '',
                  body: '',
                  previewText: ''
                })}
              >
                Add Step
              </Button>
            </div>

            {fields.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', border: '1px dashed #d9d9d9', borderRadius: '4px' }}>
                <Text type="secondary">No steps added yet. Click "Add Step" to create the first email step.</Text>
              </div>
            )}

            {fields.map((field, index) => (
              <Card
                key={field.key}
                size="small"
                style={{ marginBottom: 16 }}
                title={`Step ${index + 1}`}
                extra={
                  <Button
                    type="link"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                  >
                    Remove
                  </Button>
                }
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'delayDays']}
                      label="Delay (Days)"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input type="number" min={0} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'delayHours']}
                      label="Delay (Hours)"
                    >
                      <Input type="number" min={0} max={23} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'subject']}
                      label="Subject"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input placeholder="Email subject line" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'previewText']}
                      label="Preview Text"
                    >
                      <Input placeholder="Preview text (optional)" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'body']}
                      label="Email Body (HTML)"
                      rules={[{ required: true, message: 'Required' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <EmailBodyEditor 
                        field={field}
                        form={form}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item {...field} name={[field.name, 'id']} hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'type']} initialValue="email" hidden>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
          </div>
        )}
      </Form.List>
    )
  }

  const renderPreview = () => {
    if (!previewTemplate) return null

    return (
      <div>
        <Title level={4}>{previewTemplate.name}</Title>
        {previewTemplate.description && <Text type="secondary">{previewTemplate.description}</Text>}
        <Divider />
        
        <Tabs>
          {previewTemplate.steps.map((step, index) => (
            <Tabs.TabPane tab={`Step ${index + 1} (${step.delayDays} days)`} key={index}>
              <div>
                <Text strong>Subject:</Text>
                <div style={{ marginBottom: 16 }}>
                  {step.subject.replace(/\{\{(\w+)\}\}/g, (match, key) => {
                    const sampleValues: Record<string, string> = {
                      firstName: 'John',
                      lastName: 'Doe',
                      companyName: 'Example Business',
                      businessName: 'ServiceFlow Pro',
                      businessPhone: '555-1234',
                      businessEmail: 'info@example.com'
                    }
                    return sampleValues[key] || match
                  })}
                </div>
                
                <Text strong>Body:</Text>
                <div
                  style={{
                    marginTop: 16,
                    padding: 16,
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    background: '#fafafa',
                    minHeight: '300px'
                  }}
                  dangerouslySetInnerHTML={{
                    __html: step.body.replace(/\{\{(\w+)\}\}/g, (match, key) => {
                      const sampleValues: Record<string, string> = {
                        firstName: 'John',
                        lastName: 'Doe',
                        companyName: 'Example Business',
                        businessName: 'ServiceFlow Pro',
                        businessPhone: '555-1234',
                        businessEmail: 'info@example.com',
                        unsubscribeUrl: '#unsubscribe'
                      }
                      return sampleValues[key] || match
                    })
                  }}
                />
              </div>
            </Tabs.TabPane>
          ))}
        </Tabs>
      </div>
    )
  }

  return (
    <div>
      <Card>
        <Space style={{ marginBottom: 24, width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/marketing/campaigns')}
            >
              Back
            </Button>
            {templateType === 'system' ? (
              <Title level={3} style={{ margin: 0 }}>
                ServiceFlow Pro Templates
              </Title>
            ) : selectedBusiness ? (
              <Title level={3} style={{ margin: 0 }}>
                Templates for {selectedBusiness.name}
              </Title>
            ) : (
              <Title level={3} style={{ margin: 0 }}>
                Marketing Email Templates
              </Title>
            )}
          </Space>
          <Space>
            <Select
              value={templateType}
              onChange={handleTemplateTypeChange}
              style={{ width: 200 }}
            >
              <Option value="system">ServiceFlow Templates</Option>
              <Option value="business">Business Templates</Option>
            </Select>
            {(templateType === 'system' || selectedBusinessId) && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Create Template
              </Button>
            )}
          </Space>
        </Space>

        {templateType === 'system' ? (
          <div style={{ marginBottom: 24 }}>
            <Text type="secondary">
              These templates are used by ServiceFlow Pro to send marketing emails to businesses. 
              Edit them to customize the welcome series, feature announcements, and other system communications.
            </Text>
          </div>
        ) : null}

        {templateType === 'business' && !selectedBusinessId && (
          <Card style={{ marginBottom: 24, backgroundColor: '#f0f2f5' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 8 }}>
                  Select a Business
                </Text>
                <Text type="secondary">
                  Choose a business to view and manage their email campaign templates.
                </Text>
              </div>
              <Select
                placeholder="Select a business to manage templates"
                style={{ width: '100%', maxWidth: '500px' }}
                showSearch
                optionFilterProp="children"
                size="large"
                onChange={(value) => {
                  setSelectedBusinessId(value)
                  navigate(`/marketing/templates/${value}`)
                }}
                value={selectedBusinessId}
              >
                {businessesData?.map(business => (
                  <Option key={business.id} value={business.id}>
                    {business.name}
                  </Option>
                ))}
              </Select>
            </Space>
          </Card>
        )}

        {(templateType === 'system' || selectedBusinessId) && (
          <Table
            columns={columns}
            dataSource={templatesData?.templates || []}
            loading={templatesLoading}
            rowKey="id"
            pagination={{ pageSize: 20 }}
            locale={{
              emptyText: (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: 16 }}>
                    {templateType === 'system' 
                      ? 'No system templates found. Create a template to get started.'
                      : 'No templates found for this business'}
                  </Text>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                  >
                    Create Your First Template
                  </Button>
                </div>
              )
            }}
          />
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingTemplate ? 'Edit Template' : 'Create Template'}
        open={showCreateModal}
        onCancel={() => {
          setShowCreateModal(false)
          setEditingTemplate(null)
          form.resetFields()
        }}
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            campaignType: 'drip',
            isActive: true,
            steps: []
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Template Name"
                name="name"
                rules={[{ required: true, message: 'Template name is required' }]}
              >
                <Input placeholder="e.g., Welcome Series" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Template Reference Name"
                name="templateName"
                tooltip="Optional internal reference name (e.g., welcome_series)"
              >
                <Input placeholder="welcome_series" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Description"
                name="description"
              >
                <TextArea rows={2} placeholder="Template description" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Campaign Type"
                name="campaignType"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="drip">Drip Campaign (Multi-email series)</Option>
                  <Option value="one-time">One-Time (Single email)</Option>
                  <Option value="triggered">Triggered (Event-based)</Option>
                  <Option value="nurturing">Nurturing (Long-term relationship building)</Option>
                  <Option value="re-engagement">Re-Engagement (Re-engage inactive contacts)</Option>
                  <Option value="win-back">Win-Back (Win back lost customers)</Option>
                  <Option value="abandonment">Abandonment (Cart/service abandonment)</Option>
                  <Option value="welcome">Welcome (Welcome series for new customers)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Active"
                name="isActive"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Trigger Configuration</Divider>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Configure automatic triggers to auto-enroll contacts when events occur. Leave empty for manual enrollment only.
          </Text>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Trigger Event"
                name="triggerEvent"
                tooltip="Select an event that will automatically enroll contacts in this campaign"
              >
                <Select 
                  placeholder="No automatic trigger (manual enrollment only)" 
                  allowClear
                  onChange={(value) => {
                    // Reset conditions when event changes
                    if (!value) {
                      form.setFieldsValue({ triggerConditions: null })
                    }
                  }}
                >
                  <Option value="business_signup">Business Signup (New business registers)</Option>
                  <Option value="customer_created">Customer Created (New customer added)</Option>
                  <Option value="subscription_cancelled">Subscription Cancelled (Business cancels subscription)</Option>
                  <Option value="subscription_trial_ending">Trial Ending Soon (Trial ending in X days)</Option>
                  <Option value="contact_inactive">Contact Inactive (Contact hasn't engaged in X days)</Option>
                  <Option value="estimate_abandoned">Estimate Abandoned (Estimate created but not sent)</Option>
                  <Option value="invoice_abandoned">Invoice Abandoned (Invoice created but not paid)</Option>
                  <Option value="service_completed">Service Completed (Service/job completed)</Option>
                  <Option value="subscription_expired">Subscription Expired (Subscription expired)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Trigger Conditions"
                name="triggerConditions"
                tooltip="Additional conditions for the trigger"
              >
                <TriggerConditionsBuilder 
                  triggerEvent={watchedTriggerEvent}
                  value={watchedTriggerConditions}
                  onChange={(value) => form.setFieldsValue({ triggerConditions: value })}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Target Segments"
                name="targetSegments"
                tooltip="Filter contacts by segments"
              >
                <TargetSegmentsBuilder 
                  value={watchedTargetSegments}
                  onChange={(value) => form.setFieldsValue({ targetSegments: value })}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Campaign Steps</Divider>

          <Form.Item name="steps">
            {renderStepEditor()}
          </Form.Item>

          <Divider />

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingTemplate ? 'Update' : 'Create'} Template
              </Button>
              <Button onClick={() => {
                setShowCreateModal(false)
                setEditingTemplate(null)
                form.resetFields()
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Preview Modal */}
      <Modal
        title="Template Preview"
        open={showPreviewModal}
        onCancel={() => {
          setShowPreviewModal(false)
          setPreviewTemplate(null)
        }}
        footer={[
          <Button key="close" onClick={() => {
            setShowPreviewModal(false)
            setPreviewTemplate(null)
          }}>
            Close
          </Button>
        ]}
        width={900}
      >
        {renderPreview()}
      </Modal>
    </div>
  )
}

export default MarketingTemplatesPage

